import React, { PropsWithChildren, useMemo } from "react";
import { tokens } from "./tokens";

type CssVariableMap = Record<string, string>;

function toKebabCase(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

function flattenTokenVariables(
  prefix: string,
  source: Record<string, unknown>,
  path: string[] = [],
): CssVariableMap {
  return Object.entries(source).reduce<CssVariableMap>((acc, [key, value]) => {
    const nextPath = [...path, key];

    if (typeof value === "string" || typeof value === "number") {
      acc[`--${prefix}-${nextPath.join("-")}`] = String(value);
      return acc;
    }

    if (value && typeof value === "object") {
      Object.assign(
        acc,
        flattenTokenVariables(prefix, value as Record<string, unknown>, nextPath),
      );
    }

    return acc;
  }, {});
}

const DesignSystemVariables: React.FC<PropsWithChildren> = ({ children }) => {
  const cssVariables = useMemo(() => {
    return {
      ...flattenTokenVariables(
        "color",
        tokens.colors as unknown as Record<string, unknown>,
      ),
      ...flattenTokenVariables(
        "color-button",
        tokens.buttonColors as unknown as Record<string, unknown>,
      ),
      ...Object.entries(
        tokens.buttonColors as unknown as Record<string, unknown>,
      ).reduce<CssVariableMap>((acc, [key, value]) => {
        if (typeof value === "string" || typeof value === "number") {
          acc[`--color-button-${toKebabCase(key)}`] = String(value);
        }
        return acc;
      }, {}),
      ...flattenTokenVariables(
        "font",
        tokens.typography as unknown as Record<string, unknown>,
      ),
      ...flattenTokenVariables(
        "size",
        tokens.spacing as unknown as Record<string, unknown>,
      ),
      ...flattenTokenVariables(
        "size",
        tokens.controls as unknown as Record<string, unknown>,
      ),
      ...flattenTokenVariables(
        "border-radius",
        tokens.borderRadius as unknown as Record<string, unknown>,
      ),
      ...flattenTokenVariables(
        "shadow",
        tokens.shadows as unknown as Record<string, unknown>,
      ),
      ...flattenTokenVariables(
        "transition",
        tokens.transitions as unknown as Record<string, unknown>,
      ),
    };
  }, []);

  return (
    <div style={{ ...cssVariables, backgroundColor: "transparent" }}>
      {children}
    </div>
  );
};

export default DesignSystemVariables;
